import { Pipe, PipeTransform } from '@angular/core';

const MIN_CONTRAST = 4.5;
const DARK_TEXT = '#000000';
const LIGHT_TEXT = '#ffffff';
const DARK_RGB = [0, 0, 0];
const LIGHT_RGB = [255, 255, 255];

/**
 * Text colour over a client-chosen background: the chosen colour while it reads at the
 * minimum contrast, otherwise black or white — per SPEC-2026-10-platform-font-legibility RF-19.
 */
@Pipe({
  name: 'readableText',
  standalone: false,
})
export class ReadableTextPipe implements PipeTransform {

  transform(background: string | null | undefined, chosen?: string | null): string | null {
    const backgroundRgb = parseHex(background);
    if (!backgroundRgb) {
      return chosen ?? null;
    }
    const chosenRgb = parseHex(chosen);
    if (chosenRgb && contrastRatio(chosenRgb, backgroundRgb) >= MIN_CONTRAST) {
      return chosen;
    }
    return contrastRatio(DARK_RGB, backgroundRgb) >= contrastRatio(LIGHT_RGB, backgroundRgb)
      ? DARK_TEXT
      : LIGHT_TEXT;
  }

}

function parseHex(value: string | null | undefined): number[] | null {
  const hex = value?.trim().replace(/^#/, '') ?? '';
  if (!/^(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(hex)) {
    return null;
  }
  const digits = hex.length === 3 ? hex.split('').map((digit) => digit + digit).join('') : hex;
  return [0, 2, 4].map((start) => parseInt(digits.slice(start, start + 2), 16));
}

function relativeLuminance(rgb: number[]): number {
  const [red, green, blue] = rgb.map((channel) => {
    const value = channel / 255;
    return value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
}

function contrastRatio(first: number[], second: number[]): number {
  const lighter = Math.max(relativeLuminance(first), relativeLuminance(second));
  const darker = Math.min(relativeLuminance(first), relativeLuminance(second));
  return (lighter + 0.05) / (darker + 0.05);
}
